/**
 * ========================================
 * API ERROR HANDLING UTILITY
 * ========================================
 * Centralized error handling for FastAPI responses
 * Provides professional error messages for users
 */

import { APIErrorResponse } from '@/types/api.types';
import {
  ErrorCode,
  ErrorCategory,
  ERROR_MESSAGES,
  HTTP_STATUS_ERROR_MAP,
  BACKEND_ERROR_PATTERNS,
  AppError,
} from '@/types/errors';

export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public userMessage: string,
    public details?: Record<string, any>,
    public timestamp?: string
  ) {
    super(message);
    this.name = 'APIError';
    Object.setPrototypeOf(this, APIError.prototype);
  }

  /**
   * Check if error is from API (not network error)
   */
  isAPIError(): boolean {
    return this.status !== undefined && this.status !== 0;
  }

  /**
   * Check if error is network related
   */
  isNetworkError(): boolean {
    return this.status === 0;
  }

  /**
   * Check if error is authentication related
   */
  isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * Check if error is authorization related
   */
  isAuthzError(): boolean {
    return this.status === 403;
  }

  /**
   * Check if error is validation error
   */
  isValidationError(): boolean {
    return this.status === 422;
  }

  /**
   * Check if error is conflict (resource exists)
   */
  isConflictError(): boolean {
    return this.status === 409;
  }

  /**
   * Check if error is server error
   */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Get user-friendly message (already set in constructor)
   */
  getUserMessage(): string {
    return this.userMessage;
  }

  /**
   * Get error category
   */
  getCategory(): ErrorCategory {
    if (this.isNetworkError()) return ErrorCategory.NETWORK_ERROR;
    if (this.isAuthError()) return ErrorCategory.AUTHENTICATION;
    if (this.isAuthzError()) return ErrorCategory.AUTHORIZATION;
    if (this.status === 404) return ErrorCategory.NOT_FOUND;
    if (this.isConflictError()) return ErrorCategory.CONFLICT;
    if (this.isValidationError()) return ErrorCategory.VALIDATION;
    if (this.isServerError()) return ErrorCategory.SERVER_ERROR;
    return ErrorCategory.UNKNOWN;
  }

  /**
   * Convert to AppError for standardized error handling
   */
  toAppError(): AppError {
    return {
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      status: this.status,
      details: this.details,
      timestamp: this.timestamp || new Date().toISOString(),
      isRecoverable: !this.isServerError(),
    };
  }
}

/**
 * "date_of_birth" -> "Date of birth" -- for prefixing a FastAPI/Pydantic validation
 * error's field name (err.loc's last segment) onto its message, e.g. "Field required"
 * alone (Pydantic's literal default message) becomes "Date of birth: Field required".
 */
function formatFieldName(field: string): string {
  const spaced = String(field).replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Parse backend error message and match with known patterns
 */
function parseBackendError(errorMessage: string | undefined): {
  code: string;
  userMessage: string;
} {
  if (!errorMessage) {
    return {
      code: ErrorCode.UNKNOWN_ERROR,
      userMessage: ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR],
    };
  }

  // Check against known error patterns
  for (const pattern of BACKEND_ERROR_PATTERNS) {
    if (pattern.pattern.test(errorMessage)) {
      return {
        code: pattern.code,
        userMessage: pattern.message,
      };
    }
  }

  // If no pattern matched, return the error message as is (or generic if empty)
  return {
    code: ErrorCode.UNKNOWN_ERROR,
    userMessage: errorMessage || ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR],
  };
}

/**
 * Handle API errors from axios responses
 * Provides specific user-friendly messages based on error type
 */
export function handleAPIError(error: any): never {
  // Network error (no response)
  if (!error.response) {
    console.error('[API Network Error]', error.message);
    const parsed = parseBackendError(error.message);
    throw new APIError(
      0,
      parsed.code,
      error.message || 'Network request failed',
      parsed.userMessage,
      { originalError: error }
    );
  }

  // API responded with error status
  const { status, data } = error.response;

  // Initialize variables
  let code = HTTP_STATUS_ERROR_MAP[status] || ErrorCode.UNKNOWN_ERROR;
  let message = 'An error occurred';
  let userMessage = ERROR_MESSAGES[code];
  let details: Record<string, any> = {};

  // FastAPI error format
  if (data && typeof data === 'object') {
    // Extract message from various possible fields
    let rawMessage = data.message || data.detail || data.error || 'An error occurred';
    
    // Convert array to string if needed
    if (Array.isArray(rawMessage)) {
      message = rawMessage.map((err: any) => {
        if (typeof err === 'string') return err;
        // FastAPI/Pydantic validation error: { loc: ["body", "email"], msg: "Field required" }.
        // msg alone is Pydantic's bare, unhelpful default ("Field required" says nothing about
        // WHICH field) -- prefix the field name (loc's last segment) whenever both are present.
        if (Array.isArray(err.loc) && err.loc.length > 0 && err.msg) {
          const fieldName = err.loc[err.loc.length - 1];
          return `${formatFieldName(fieldName)}: ${err.msg}`;
        }
        if (err.msg) return err.msg;
        if (err.message) return err.message;
        return JSON.stringify(err);
      }).join(', ');
    } else if (rawMessage && typeof rawMessage === 'object') {
      // A structured business-error payload, e.g. `raise HTTPException(409, detail=claim)`
      // where claim is a dict like { ok: false, code: 'SESSION_ALREADY_ACTIVE', message: '...' }
      // (see voice_usage_router.py). String(rawMessage) here would silently produce the
      // useless literal "[object Object]" -- pull its own message/detail field instead, and
      // keep the whole object in `details` so callers that need the structured fields (e.g.
      // `.code`) can still get at them via APIError.details, same place validation field
      // errors already live.
      message = rawMessage.message || rawMessage.detail || rawMessage.error || JSON.stringify(rawMessage);
      details = { ...details, ...rawMessage };
    } else {
      message = String(rawMessage);
    }

    // Try to parse backend error for specific handling
    const parsed = parseBackendError(message);
    code = parsed.code;
    userMessage = parsed.userMessage;

    // Extract validation errors if present
    // FastAPI Pydantic validation errors come as array with specific format
    if (Array.isArray(data.detail)) {
      // Format: [{ "loc": ["field_name"], "msg": "error message" }]
      const validationErrors: Record<string, string[]> = {};
      data.detail.forEach((err: any) => {
        if (err.loc && err.msg) {
          const fieldName = err.loc[err.loc.length - 1]; // Get last element (field name)
          if (!validationErrors[fieldName]) {
            validationErrors[fieldName] = [];
          }
          validationErrors[fieldName].push(err.msg);
        }
      });
      if (Object.keys(validationErrors).length > 0) {
        details = validationErrors;
      }
    }
    
    // Also check for errors/validation_errors fields
    if (data.errors) {
      details = { ...details, ...data.errors };
    } else if (data.validation_errors) {
      details = { ...details, ...data.validation_errors };
    }

    // Store additional details from data.details
    if (data.details) {
      details = { ...details, ...data.details };
    }

    console.error(`[API Error ${status}] Request: ${error.config?.url}`, {
      code,
      message,
      userMessage,
      details,
      rawData: data,
    });

    throw new APIError(
      status,
      code,
      message,
      userMessage,
      Object.keys(details).length > 0 ? details : undefined,
      data.timestamp
    );
  }

  // Fallback for unknown error format
  console.error(`[API Error ${status}] Raw response:`, JSON.stringify(data, null, 2));
  throw new APIError(
    status,
    code,
    typeof data === 'string' ? data : 'An error occurred',
    ERROR_MESSAGES[code],
    undefined,
    new Date().toISOString()
  );
}

/**
 * Retry logic with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Don't retry on client errors (4xx)
      if (error instanceof APIError && error.status >= 400 && error.status < 500) {
        throw error;
      }

      // Wait before retrying
      if (attempt < maxRetries - 1) {
        const delay = delayMs * Math.pow(2, attempt);
        console.warn(`[API Retry] Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Max retries exceeded');
}

/**
 * Log API errors for debugging
 */
export function logAPIError(error: APIError, context?: string): void {
  const timestamp = new Date().toISOString();
  const message = `[${timestamp}] ${context ? `[${context}] ` : ''}API Error: ${error.code} (${error.status})`;

  if (error.isServerError()) {
    console.error(message, error.message, error.details);
  } else {
    console.warn(message, error.message, error.details);
  }
}

/**
 * Transform error for UI display (returns user-friendly message)
 */
export function getErrorMessage(error: unknown, defaultMessage = 'Something went wrong'): string {
  if (error instanceof APIError) {
    return error.getUserMessage();
  }

  if (error instanceof Error) {
    return error.message;
  }

  return defaultMessage;
}

/**
 * Get validation errors from error details
 * Returns field-level error messages
 */
export function getValidationErrors(error: APIError): Record<string, string[]> {
  if (!error.details) return {};

  const errors: Record<string, string[]> = {};

  Object.entries(error.details).forEach(([field, messages]) => {
    if (Array.isArray(messages)) {
      errors[field] = messages.map(m => typeof m === 'string' ? m : JSON.stringify(m));
    } else if (typeof messages === 'string') {
      errors[field] = [messages];
    }
  });

  return errors;
}

/**
 * Check if error is recoverable (user can retry)
 */
export function isRecoverableError(error: APIError): boolean {
  // Don't retry client validation errors, but do retry server errors
  if (error.isValidationError() && !error.status.toString().startsWith('422')) {
    return false;
  }

  // Retry on server errors, conflicts (might resolve), auth errors (user can login)
  return error.isServerError() || error.isConflictError() || error.isAuthError();
}
